import React from "react";
import CustomButton from "../Input/CustomButton";

export type SubscriptionPanelMode = "normal" | "loading" | "busy" | "error";

export interface SubscriptionContext {
	/** The user's display name */
	name: string;

	/** The user's account email */
	email: string;

	/**
	 * Whether the user has an active paid subscription.
	 * Derived from the user's subscription claim status —
	 * any of the active statuses ("active", "past_due", "trialing")
	 * counts as paid. Only a null/undefined status or "free" is unpaid.
	 */
	isPaid: boolean;

	/** Display name of the current plan (e.g., "Essentials", "Team"). Required when isPaid is true. */
	planName?: string;

	/** URL to the Stripe customer portal for subscription management */
	customerPortalUrl?: string;
}

export interface SubscriptionPanelProps {
	/** User subscription context */
	user: SubscriptionContext;

	/** Called when "Cancel Subscription" is clicked (paid users only) */
	onCancel?: () => void;

	/** Called when "Manage Subscription" is clicked (paid users only) */
	onManage?: () => void;

	/** Called when "Free - Upgrade Now!" is clicked (free users only) */
	onUpgrade?: () => void;

	/** What mode the panel is in. Modes are:
	 *  - `normal` &mdash; Default appearance and behavior
	 *  - `loading` &mdash; Shows pulsing "One moment please..." text
	 *  - `busy` &mdash; Cancellation is ongoing, cancel button is disabled
	 *  - `error` &mdash; Buttons disabled, tooltip says "The server could not be reached"
	 */
	mode?: SubscriptionPanelMode;
}

const SubscriptionPanel: React.FC<SubscriptionPanelProps> = ({
	user,
	onCancel,
	onManage,
	onUpgrade,
	mode = "normal"
}) => {
	if (mode === "loading") {
		return (
			<div className="text-primary flex justify-center items-center animate-pulse h-28">
				One Moment Please...
			</div>
		);
	}

	if (!user.isPaid) {
		return (
			<CustomButton
				title="Subscription Type"
				text="Free - Upgrade Now!"
				rightIcon="External"
				onClick={onUpgrade}
			/>
		);
	}

	const tooltip = mode === "error" ? "The server could not be reached" : "";

	return (
		<div className="space-y-4" title={tooltip}>
			<CustomButton
				title={`Subscription - ${user.planName}`}
				text="Manage Subscription"
				rightIcon="External"
				onClick={onManage}
				disabled={mode === "error"}
			/>
			<CustomButton
				text="Cancel Subscription"
				onClick={onCancel}
				disabled={mode === "busy" || mode === "error"}
			/>
		</div>
	);
};

SubscriptionPanel.displayName = "SubscriptionPanel";

export default SubscriptionPanel;
